<template>
  <Card class="p-6 hover:shadow-lg transition-shadow">
    <div class="flex flex-col space-y-4">
      <!-- Header with name and status -->
      <div class="flex items-start justify-between">
        <div class="flex-1 min-w-0">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white truncate">
            {{ model.name }}
          </h3>
          <div class="mt-1 flex items-center space-x-2">
            <span
              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
            >
              ASAB
            </span>
            <code
              class="text-xs font-mono text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded"
            >
              {{ model.slug }}
            </code>
          </div>
        </div>
        <span
          :class="[
            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0',
            statusBadgeColor,
          ]"
        >
          {{ statusText }}
        </span>
      </div>

      <!-- Load Balancing Strategy -->
      <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Load Balancing</p>
        <p class="text-sm font-medium text-gray-900 dark:text-white">
          Weighted Round-Robin
        </p>
      </div>

      <!-- Routing Targets Summary -->
      <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">Routing Targets</p>
        <div class="space-y-1.5">
          <div
            v-for="target in model.routing.targets"
            :key="`${target.backend_type}-${target.backend_id}`"
            class="flex items-center justify-between text-sm"
          >
            <div class="flex items-center space-x-2 min-w-0 flex-1">
              <span
                :class="[
                  'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0',
                  target.backend_type === 'base'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                    : 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
                ]"
              >
                {{ target.backend_type === 'base' ? 'Base' : 'BYOM' }}
              </span>
              <span class="text-gray-900 dark:text-white truncate">
                {{ target.backend_id }}
              </span>
            </div>
            <span class="text-gray-500 dark:text-gray-400 font-medium flex-shrink-0 ml-2">
              {{ calculateWeightPercentage(target.weight) }}%
            </span>
          </div>
        </div>
      </div>

      <!-- Blended Cost Display -->
      <div class="grid grid-cols-2 gap-3">
        <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Blended Input Cost</p>
          <p class="text-sm font-semibold text-gray-900 dark:text-white">
            ${{ model.blended_cost.input_1m.toFixed(4)
            }}<span class="text-xs font-normal text-gray-500 dark:text-gray-400">/1M tokens</span>
          </p>
        </div>
        <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Blended Output Cost</p>
          <p class="text-sm font-semibold text-gray-900 dark:text-white">
            ${{ model.blended_cost.output_1m.toFixed(4)
            }}<span class="text-xs font-normal text-gray-500 dark:text-gray-400">/1M tokens</span>
          </p>
        </div>
      </div>

      <!-- Rate Limits Display -->
      <div v-if="hasRateLimits" class="grid grid-cols-2 gap-3">
        <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Rate Limit (RPM)</p>
          <p class="text-sm font-semibold text-gray-900 dark:text-white">
            {{ model.limits.rpm !== null ? model.limits.rpm.toLocaleString() : 'Unlimited' }}
          </p>
        </div>
        <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Rate Limit (TPM)</p>
          <p class="text-sm font-semibold text-gray-900 dark:text-white">
            {{ model.limits.tpm !== null ? model.limits.tpm.toLocaleString() : 'Unlimited' }}
          </p>
        </div>
      </div>
      <div
        v-else
        class="bg-gray-50 dark:bg-gray-800 rounded-lg p-3"
      >
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Rate Limits</p>
        <p class="text-sm text-gray-900 dark:text-white">No rate limits configured</p>
      </div>

      <!-- Fallback Chain Summary -->
      <div v-if="hasFallbackChain" class="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">Fallback Chain</p>
        <div class="flex items-center space-x-2 overflow-x-auto">
          <div
            v-for="(fallback, index) in model.routing.fallback_chain"
            :key="`${fallback.backend_type}-${fallback.backend_id}`"
            class="flex items-center space-x-2 flex-shrink-0"
          >
            <div class="flex items-center space-x-1.5">
              <span
                :class="[
                  'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium',
                  fallback.backend_type === 'base'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                    : 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
                ]"
              >
                {{ fallback.backend_type === 'base' ? 'Base' : 'BYOM' }}
              </span>
              <span class="text-sm text-gray-900 dark:text-white">
                {{ fallback.backend_id }}
              </span>
            </div>
            <svg
              v-if="index < model.routing.fallback_chain.length - 1"
              class="w-4 h-4 text-gray-400 dark:text-gray-600 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </div>
      </div>
      <div
        v-else
        class="bg-gray-50 dark:bg-gray-800 rounded-lg p-3"
      >
        <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Fallback Chain</p>
        <p class="text-sm text-gray-900 dark:text-white">No fallback configured</p>
      </div>

      <!-- Model Info -->
      <div class="flex items-center justify-between text-sm">
        <span class="text-gray-500 dark:text-gray-400">Model Type</span>
        <span class="font-medium text-gray-900 dark:text-white">ASAB (Augmented Service Access Bridge)</span>
      </div>

      <!-- Actions -->
      <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
        <div class="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            class="flex-1"
            :disabled="isProcessing"
            @click="handleEdit"
          >
            <svg
              class="w-4 h-4 mr-1.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
            Edit
          </Button>
          <!-- eslint-disable-next-line asab-design-system/no-raw-button-card-markup -- icon-only delete action in a menu, not a standalone Button -->
          <Button
            variant="ghost"
            size="sm"
            class="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            :disabled="isProcessing"
            @click="handleDelete"
          >
            <svg
              class="w-4 h-4 mr-1.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
            Delete
          </Button>
        </div>
      </div>
    </div>
  </Card>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { Card, Button } from '@asabdev/asab-ui';
  import type { AsabModelResponse } from '../types';

  interface Props {
    model: AsabModelResponse;
  }

  interface Emits {
    (e: 'edit', model: AsabModelResponse): void;
    (e: 'delete', model: AsabModelResponse): void;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<Emits>();

  // Local state
  const isProcessing = ref(false);

  // Computed properties
  const statusBadgeColor = computed(() => {
    return props.model.is_enabled
      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
      : 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300';
  });

  const statusText = computed(() => {
    return props.model.is_enabled ? 'Enabled' : 'Disabled';
  });

  const hasRateLimits = computed(() => {
    return props.model.limits.rpm !== null || props.model.limits.tpm !== null;
  });

  const hasFallbackChain = computed(() => {
    return props.model.routing.fallback_chain && props.model.routing.fallback_chain.length > 0;
  });

  // Methods
  const calculateWeightPercentage = (weight: number): string => {
    const totalWeight = props.model.routing.targets.reduce((sum, t) => sum + t.weight, 0);
    if (totalWeight === 0) return '0';
    return ((weight / totalWeight) * 100).toFixed(1);
  };

  const handleEdit = () => {
    if (isProcessing.value) return;
    emit('edit', props.model);
  };

  const handleDelete = () => {
    if (isProcessing.value) return;
    emit('delete', props.model);
  };
</script>
